
import { recordComponentRender, getSuspectedLeaks } from './detection/leakDetection';

// Memory leak detection thresholds
const RENDERS_THRESHOLD = 100;
const INSTANCE_THRESHOLD = 20;

// Track component renders for leak detection
let componentRenders = new Map<string, number>();
let componentInstances = new Map<string, Set<string>>();

/**
 * Record a component render event
 * @param componentName Name of the component that rendered
 * @param instanceId Unique identifier for component instance
 * @param renderTime Time taken to render (ms)
 */
export function trackComponentRender(
  componentName: string,
  instanceId: string,
  renderTime: number
): void {
  // Update render count
  const currentCount = componentRenders.get(componentName) || 0;
  componentRenders.set(componentName, currentCount + 1);

  // Track component instances to detect multiple instances
  if (!componentInstances.has(componentName)) {
    componentInstances.set(componentName, new Set());
  }
  componentInstances.get(componentName)?.add(instanceId);

  // Track in more detailed system
  recordComponentRender(componentName, renderTime, instanceId);
}

/**
 * Reset tracking for a component
 */
export function resetComponentTracking(componentName: string): void {
  componentRenders.delete(componentName);
  componentInstances.delete(componentName);
}

/**
 * Get components with potential memory leaks
 */
export function detectPotentialLeaks(): { 
  componentName: string; 
  renderCount: number; 
  instanceCount: number; 
}[] {
  const potentialLeaks: { 
    componentName: string; 
    renderCount: number; 
    instanceCount: number; 
  }[] = [];

  componentRenders.forEach((renderCount, componentName) => {
    const instanceCount = componentInstances.get(componentName)?.size || 0;

    if (renderCount > RENDERS_THRESHOLD || instanceCount > INSTANCE_THRESHOLD) {
      potentialLeaks.push({
        componentName,
        renderCount,
        instanceCount
      });
    }
  });

  return potentialLeaks;
}

/**
 * Run comprehensive memory leak detection
 */
export function detectMemoryLeaks(): void {
  const potentialLeaks = detectPotentialLeaks();
  const suspectedLeaks = getSuspectedLeaks();

  if (potentialLeaks.length > 0 || suspectedLeaks.size > 0) {
    console.warn('⚠️ Potential memory leaks detected:');
    
    if (potentialLeaks.length > 0) {
      console.warn('Based on render counts:');
      potentialLeaks.forEach(leak => {
        console.warn(
          `- ${leak.componentName}: ${leak.renderCount} renders, ${leak.instanceCount} instances`
        );
      });
    }
    
    if (suspectedLeaks.size > 0) {
      console.warn('Based on detailed analysis:');
      suspectedLeaks.forEach((data, component) => {
        console.warn(
          `- ${component}: ${data.renderCount} renders, ${data.instanceCount} instances, ` +
          `${data.averageRenderTime.toFixed(2)}ms average render time (${data.trend} trend)`
        );
      });
    }
  }
}

export default {
  trackComponentRender,
  resetComponentTracking,
  detectPotentialLeaks,
  detectMemoryLeaks
};
